import React, { useState, useMemo, useCallback } from "react";
import {
  Layers,
  Search,
  Filter,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  Boxes,
  Calendar,
  Building2,
  FileText,
  Scissors,
  Factory,
  ArrowRight,
  ExternalLink,
  ChevronRight,
  Info,
  Check,
  X,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { trpcClient } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";
import { cn, formatNumber } from "@/lib/utils";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import {
  type PoolItem,
  checkHomogeneity,
  calculateGangRunSheets,
  calculateSurplusPerProduct,
} from "./packaging-combine.engine";
import { toast } from "sonner";

interface OrderPoolWorkbenchProps {
  onSelectItemsForPO: (
    items: PoolItem[],
    mode: "SINGLE" | "GANG_RUN" | "SPLIT",
    splitQty?: number
  ) => void;
  onOpenLegacyModal?: () => void;
  onManualEntry?: () => void;
  /** Bình file ngay trong trang tạo lệnh (dòng chưa có khổ in / số con cũng chọn được để bình) */
  onPrepress?: (items: PoolItem[]) => void;
}

export function OrderPoolWorkbench({
  onSelectItemsForPO,
  onOpenLegacyModal,
  onManualEntry,
  onPrepress,
}: OrderPoolWorkbenchProps) {
  // — Filters —
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCustomer, setFilterCustomer] = useState<string>("ALL");
  const [filterMaterial, setFilterMaterial] = useState<string>("ALL");
  const [filterGate, setFilterGate] = useState<"ALL" | "VALID" | "INVALID">("ALL");

  // — Selection state —
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // — Split Dialog state —
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [itemToSplit, setItemToSplit] = useState<PoolItem | null>(null);
  const [splitQtyInput, setSplitQtyInput] = useState<number>(0);

  // — Query pending items pool —
  const { data: poolData, isLoading: isLoadingPool, refetch } = useQuery({
    queryKey: [
      "production-orders-pending-items-pool",
      filterCustomer,
      filterMaterial,
      searchTerm,
    ],
    queryFn: async () => {
      return await trpcClient.productionOrders.getPendingItemsPool.query({
        customerIds: filterCustomer !== "ALL" ? [filterCustomer] : undefined,
        materialId: filterMaterial !== "ALL" ? filterMaterial : undefined,
        search: searchTerm.trim() || undefined,
      });
    },
  });

  // — Query customers lookup for filter toolbar —
  const { data: customersData } = useQuery({
    queryKey: ["customers-lookup-for-pool"],
    queryFn: async () => {
      const res = await trpcClient.customers.list.query({ limit: 100 });
      return (res as any)?.items || (Array.isArray(res) ? res : []);
    },
    staleTime: 5 * 60 * 1000,
  });

  const allItems: PoolItem[] = (poolData?.items as any) || [];

  // — Client filtering for Gate 1 status —
  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      if (filterGate === "VALID") return item.isValidForProduction;
      if (filterGate === "INVALID") return !item.isValidForProduction;
      return true;
    });
  }, [allItems, filterGate]);

  // — Selected items list & homogeneity check —
  const selectedItems = useMemo(() => {
    return allItems.filter((item) => selectedIds.has(item.id));
  }, [allItems, selectedIds]);

  // Lập lệnh chỉ khi mọi dòng đã chọn đủ khổ in + số con; dòng chưa đủ chỉ để bình file
  const allSelectedValid = selectedItems.every((i) => i.isValidForProduction);

  const homogeneity = useMemo(() => {
    return checkHomogeneity(selectedItems);
  }, [selectedItems]);

  const gangStats = useMemo(() => {
    if (selectedItems.length === 0) return null;
    return calculateGangRunSheets(
      selectedItems.map((i) => ({
        id: i.id,
        quantity: i.remainingPlanQty,
        ups: i.effectiveUps || 1,
        lengthMm: i.flatLength || i.length || 100,
        widthMm: i.flatWidth || i.width || 100,
      }))
    );
  }, [selectedItems]);

  const surplusStats = useMemo(() => {
    if (!gangStats || selectedItems.length <= 1) return [];
    return calculateSurplusPerProduct(
      selectedItems.map((i) => ({
        id: i.id,
        quantity: i.remainingPlanQty,
        ups: i.effectiveUps || 1,
      })),
      gangStats.totalRunSheetsNeeded
    );
  }, [selectedItems, gangStats]);

  // — Handlers —
  const handleToggleSelect = useCallback((id: string, isValid: boolean) => {
    if (!isValid && !onPrepress) {
      toast.warning("Mặt hàng này chưa đủ thông số khổ in hoặc số UP để tạo lệnh.");
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, [onPrepress]);

  const handleSelectAllValid = useCallback(() => {
    const validIds = filteredItems
      .filter((i) => i.isValidForProduction)
      .map((i) => i.id);
    setSelectedIds((prev) => {
      const allSelected = validIds.every((id) => prev.has(id));
      if (allSelected) {
        // Unselect all in view
        const next = new Set(prev);
        validIds.forEach((id) => next.delete(id));
        return next;
      } else {
        // Select all valid in view
        const next = new Set(prev);
        validIds.forEach((id) => next.add(id));
        return next;
      }
    });
  }, [filteredItems]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Dispatch Single PO
  const handleDispatchSingle = useCallback(() => {
    if (selectedItems.length !== 1) return;
    const item = selectedItems[0];
    onSelectItemsForPO([item], "SINGLE");
  }, [selectedItems, onSelectItemsForPO]);

  // Dispatch Gang-Run PO
  const handleDispatchGangRun = useCallback(() => {
    if (selectedItems.length < 2) return;
    if (!homogeneity.canCombine) {
      toast.error(homogeneity.errors[0] || "Không thể ghép bài do vi phạm chất liệu.");
      return;
    }
    onSelectItemsForPO(selectedItems, "GANG_RUN");
  }, [selectedItems, homogeneity, onSelectItemsForPO]);

  // Open Split Dialog
  const handleOpenSplitDialog = useCallback((item: PoolItem) => {
    setItemToSplit(item);
    setSplitQtyInput(Math.min(item.remainingPlanQty, Math.ceil(item.remainingPlanQty / 2)));
    setSplitDialogOpen(true);
  }, []);

  // Confirm Split Dispatch
  const handleConfirmSplit = useCallback(() => {
    if (!itemToSplit) return;
    if (splitQtyInput <= 0 || splitQtyInput > itemToSplit.remainingPlanQty) {
      toast.error(`Số lượng tách phải từ 1 đến ${itemToSplit.remainingPlanQty.toLocaleString()} cái`);
      return;
    }
    setSplitDialogOpen(false);
    onSelectItemsForPO([itemToSplit], "SPLIT", splitQtyInput);
  }, [itemToSplit, splitQtyInput, onSelectItemsForPO]);

  return (
    <div className="w-full space-y-3">
      {/* TOOLBAR TẦNG 1: BỘ LỌC ĐA CHIỀU & THỐNG KÊ NHANH */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-2xs space-y-2.5">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2.5">
          {/* Cụm tìm kiếm */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Tìm theo tên sản phẩm, mã SP, mã đơn hàng, bài bình..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8.5 h-8.5 text-xs bg-slate-50/60 focus:bg-white"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Cụm dropdowns lọc */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Lọc Khách hàng */}
            <select
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
              className="h-8.5 text-xs border border-slate-200 rounded-md px-2.5 bg-slate-50/60 hover:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 font-medium text-slate-700"
            >
              <option value="ALL">Tất cả Khách hàng</option>
              {customersData?.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.code ? `[${c.code}] ` : ""}
                  {c.name}
                </option>
              ))}
            </select>

            {/* Lọc Gate 1 Status */}
            <select
              value={filterGate}
              onChange={(e) => setFilterGate(e.target.value as any)}
              className="h-8.5 text-xs border border-slate-200 rounded-md px-2.5 bg-slate-50/60 hover:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 font-medium text-slate-700"
            >
              <option value="ALL">Tất cả trạng thái Gate 1</option>
              <option value="VALID">✅ Đủ điều kiện (Có khổ in & UPS)</option>
              <option value="INVALID">⚠️ Cần bổ sung quy cách</option>
            </select>

            {/* Nút reset */}
            {(searchTerm || filterCustomer !== "ALL" || filterGate !== "ALL") && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm("");
                  setFilterCustomer("ALL");
                  setFilterGate("ALL");
                }}
                className="h-8.5 px-2 text-xs text-slate-500 hover:text-slate-800"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Đặt lại
              </Button>
            )}

            {/* Nút mở modal Prepress cũ nếu cần */}
            {onOpenLegacyModal && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onOpenLegacyModal}
                className="h-8.5 px-2.5 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50 font-medium"
              >
                <Layers className="h-3.5 w-3.5 mr-1" /> Xem theo bài bình
              </Button>
            )}

            {/* Nút nhập form thủ công */}
            {onManualEntry && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onManualEntry}
                className="h-8.5 px-2.5 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50 font-medium"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Nhập thủ công
              </Button>
            )}
          </div>
        </div>

        {/* Dải Metrics Bar Tầng 1 (Gate 1 Status Tracker) */}
        <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-xs text-slate-600 flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-medium text-slate-700">
              Tổng mặt hàng chờ:{" "}
              <strong className="font-mono text-slate-900">
                {poolData?.totalItems || allItems.length}
              </strong>
            </span>
            <span>•</span>
            <span className="inline-flex items-center gap-1 text-emerald-700 font-medium bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200/60">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              Sẵn sàng lập lệnh:{" "}
              <strong className="font-mono">{poolData?.validCount ?? 0}</strong>
            </span>
            <span>•</span>
            <span className="inline-flex items-center gap-1 text-amber-800 font-medium bg-amber-50 px-2 py-0.5 rounded border border-amber-200/60">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
              Chưa đủ quy cách:{" "}
              <strong className="font-mono">{poolData?.invalidCount ?? 0}</strong>
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectAllValid}
              className="h-7 text-[11px] text-slate-700 border-slate-200 hover:bg-slate-50 font-medium"
            >
              Chọn/Bỏ chọn tất cả hợp lệ
            </Button>
            {selectedIds.size > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearSelection}
                className="h-7 text-[11px] text-red-600 hover:bg-red-50 font-medium"
              >
                Bỏ chọn ({selectedIds.size})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* BẢNG DỮ LIỆU ĐIỀU PHỐI (ORDER POOL WORKBENCH GRID) */}
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden shadow-2xs">
        <div className="overflow-x-auto max-h-[58vh]">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-slate-50/90 text-slate-600 sticky top-0 z-20 border-b border-slate-200">
              <tr>
                {/* Checkbox Sticky */}
                <th
                  className="w-10 px-3 py-2.5 text-center sticky left-0 z-30"
                  style={{
                    backgroundColor: "#f8fafc",
                    isolation: "isolate",
                  }}
                >
                  <Checkbox
                    checked={
                      filteredItems.length > 0 &&
                      filteredItems
                        .filter((i) => i.isValidForProduction)
                        .every((i) => selectedIds.has(i.id)) &&
                      filteredItems.filter((i) => i.isValidForProduction).length > 0
                    }
                    onCheckedChange={handleSelectAllValid}
                    aria-label="Chọn tất cả"
                  />
                </th>
                <th className="px-3 py-2.5 font-semibold text-slate-700 min-w-[130px]">
                  Cổng Gate 1
                </th>
                <th className="px-3 py-2.5 font-semibold text-slate-700 min-w-[200px]">
                  Khách Hàng & Đơn Hàng
                </th>
                <th className="px-3 py-2.5 font-semibold text-slate-700 min-w-[240px]">
                  Sản Phẩm & Quy Cách
                </th>
                <th className="px-3 py-2.5 font-semibold text-slate-700 min-w-[170px]">
                  Khổ In & Bài Bình
                </th>
                <th className="px-3 py-2.5 font-semibold text-slate-700 min-w-[130px]">
                  Chất Liệu Giấy
                </th>
                <th className="px-3 py-2.5 font-semibold text-slate-700 text-right min-w-[110px]">
                  SL Còn Lại
                </th>
                <th className="px-3 py-2.5 font-semibold text-slate-700 text-right min-w-[100px]">
                  Dự Kiến Tờ In
                </th>
                <th className="px-3 py-2.5 font-semibold text-slate-700 text-center min-w-[90px]">
                  Thao Tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoadingPool ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="p-3 text-center">
                      <Skeleton className="h-4 w-4 mx-auto rounded" />
                    </td>
                    <td className="p-3">
                      <Skeleton className="h-5 w-24 rounded" />
                    </td>
                    <td className="p-3">
                      <Skeleton className="h-4 w-32 mb-1" />
                      <Skeleton className="h-3 w-20" />
                    </td>
                    <td className="p-3">
                      <Skeleton className="h-4 w-48 mb-1" />
                      <Skeleton className="h-3 w-24" />
                    </td>
                    <td className="p-3">
                      <Skeleton className="h-4 w-28" />
                    </td>
                    <td className="p-3">
                      <Skeleton className="h-4 w-24" />
                    </td>
                    <td className="p-3 text-right">
                      <Skeleton className="h-4 w-16 ml-auto" />
                    </td>
                    <td className="p-3 text-right">
                      <Skeleton className="h-4 w-16 ml-auto" />
                    </td>
                    <td className="p-3 text-center">
                      <Skeleton className="h-6 w-12 mx-auto rounded" />
                    </td>
                  </tr>
                ))
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-slate-500">
                    <Boxes className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    <p className="font-medium text-slate-700">
                      Không có mặt hàng nào phù hợp bộ lọc
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Hãy thay đổi điều kiện tìm kiếm hoặc chọn khách hàng khác
                    </p>
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const isSelected = selectedIds.has(item.id);
                  const isValid = item.isValidForProduction;

                  return (
                    <tr
                      key={item.id}
                      onClick={() => handleToggleSelect(item.id, isValid)}
                      className={cn(
                        "transition-colors cursor-pointer",
                        isSelected
                          ? "bg-indigo-50/50 hover:bg-indigo-50/70"
                          : isValid
                          ? "hover:bg-slate-50/70"
                          : "bg-slate-50/30 text-slate-400 hover:bg-slate-50/50"
                      )}
                    >
                      {/* Checkbox Sticky */}
                      <td
                        className="px-3 py-2.5 text-center sticky left-0 z-10"
                        style={{
                          backgroundColor: isSelected
                            ? "#eef2ff"
                            : isValid
                            ? "#ffffff"
                            : "#f8fafc",
                          isolation: "isolate",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={!isValid && !onPrepress}
                          onCheckedChange={() => handleToggleSelect(item.id, isValid)}
                          aria-label={`Chọn ${item.productName}`}
                        />
                      </td>

                      {/* Cổng Gate 1 Badge */}
                      <td className="px-3 py-2.5">
                        {isValid ? (
                          <Badge
                            variant="outline"
                            className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-medium inline-flex items-center gap-1 shadow-2xs"
                          >
                            <Check className="h-2.5 w-2.5" /> Sẵn sàng lệnh
                          </Badge>
                        ) : (
                          <div className="flex flex-col items-start gap-1">
                            <Badge
                              variant="outline"
                              className="bg-amber-50 text-amber-800 border-amber-300 text-[10px] font-medium inline-flex items-center gap-1 shadow-2xs"
                            >
                              <AlertTriangle className="h-2.5 w-2.5 text-amber-600" />
                              {item.missingReason || "Thiếu khổ in/UPS"}
                            </Badge>
                            {item.productId && (
                              <a
                                href={`/products/${item.productId}/edit`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-[10px] text-indigo-600 hover:text-indigo-800 underline inline-flex items-center gap-0.5"
                              >
                                ✏️ Bổ sung quy cách <ExternalLink className="h-2.5 w-2.5" />
                              </a>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Khách hàng & Đơn hàng */}
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-800 truncate max-w-[190px]">
                          {item.customerName}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500">
                          <span className="font-mono text-indigo-600 font-medium">
                            {item.orderCode}
                          </span>
                          {item.deliveryDeadline && (
                            <>
                              <span>•</span>
                              <span className="inline-flex items-center gap-0.5 text-slate-500">
                                <Calendar className="h-3 w-3 text-slate-400" />
                                {new Date(item.deliveryDeadline).toLocaleDateString(
                                  "vi-VN"
                                )}
                              </span>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Sản phẩm & Quy cách */}
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <ProductionThumbnailCell
                            imageUrl={item.productImage}
                            title={item.productName}
                            subtitle={item.productCode || undefined}
                            type="product"
                            size={32}
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate max-w-[210px]">
                              {item.productName}
                            </div>
                            <div className="font-mono text-[11px] text-slate-500">
                              {item.productCode || item.detailId || "-"}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Khổ in & Bài bình */}
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {item.effectivePaperSize ? (
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] font-mono"
                            >
                              Khổ: {item.effectivePaperSize}
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-red-50 text-red-600 border-red-200 text-[10px]"
                            >
                              Chưa có khổ in
                            </Badge>
                          )}

                          {item.effectiveUps ? (
                            <Badge
                              variant="outline"
                              className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] font-mono font-semibold"
                            >
                              {item.effectiveUps} UPS
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-red-50 text-red-600 border-red-200 text-[10px]"
                            >
                              Thiếu UPS
                            </Badge>
                          )}
                        </div>
                        {item.prepressCode && (
                          <div className="mt-1 text-[11px] font-mono text-emerald-700 font-semibold inline-flex items-center gap-1">
                            <Layers className="h-3 w-3 text-emerald-600" />
                            {item.prepressCode}
                          </div>
                        )}
                      </td>

                      {/* Chất liệu giấy */}
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-slate-800 truncate max-w-[120px]">
                          {item.materialName || item.materialCode || "Mặc định theo SP"}
                        </div>
                        {item.cutFormat && (
                          <div className="text-[10px] text-slate-400 font-mono">
                            Mộc: {item.cutFormat}
                          </div>
                        )}
                      </td>

                      {/* SL Còn lại */}
                      <td className="px-3 py-2.5 text-right">
                        <div className="font-mono font-bold text-slate-900 text-xs">
                          {formatNumber(item.remainingPlanQty)}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          Tổng: {formatNumber(item.quantity)} {item.unit || "hộp"}
                        </div>
                      </td>

                      {/* Dự kiến tờ in */}
                      <td className="px-3 py-2.5 text-right">
                        {item.calculatedSheets ? (
                          <div className="font-mono text-indigo-700 font-semibold">
                            {formatNumber(item.calculatedSheets)} tờ
                          </div>
                        ) : (
                          <span className="text-slate-300 font-mono">-</span>
                        )}
                      </td>

                      {/* Thao tác dòng */}
                      <td
                        className="px-3 py-2.5 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isValid ? (
                          <div className="flex items-center justify-center gap-1">
                            {onPrepress && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                title={item.prepressCode ? `Sửa / ghép lại bài bình ${item.prepressCode}` : "Bình file cho mặt hàng này"}
                                onClick={() => onPrepress([item])}
                                className="h-6 w-6 p-0 text-slate-500 hover:text-emerald-700 hover:bg-emerald-50"
                                data-testid="pool-row-prepress"
                              >
                                <Layers className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              title="Tách đợt lệnh (Split PO)"
                              onClick={() => handleOpenSplitDialog(item)}
                              className="h-6 w-6 p-0 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
                            >
                              <Scissors className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              title="Tạo Lệnh Đơn ngay"
                              onClick={() => onSelectItemsForPO([item], "SINGLE")}
                              className="h-6 px-1.5 text-[10px] text-indigo-600 border-indigo-200 hover:bg-indigo-50 font-medium"
                            >
                              Lên lệnh
                            </Button>
                          </div>
                        ) : onPrepress ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            title="Bình file để có khổ in và số con, rồi lập lệnh"
                            onClick={() => onPrepress([item])}
                            className="h-6 px-1.5 text-[10px] text-emerald-700 border-emerald-200 hover:bg-emerald-50 font-medium"
                            data-testid="pool-row-prepress"
                          >
                            <Layers className="h-3 w-3 mr-0.5" /> Bình file
                          </Button>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">Khóa</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* FLOATING BULK DISPATCH ACTION BAR (Chuẩn Rule 9 Enterprise) */}
      {selectedItems.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 duration-200 w-auto max-w-[94vw]">
          <div className="backdrop-blur-md bg-slate-900/95 text-white shadow-2xl rounded-2xl border border-slate-700/60 px-4 py-2.5 flex items-center gap-3.5 sm:gap-4 flex-wrap">
            {/* Badge đếm */}
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center h-6 min-w-[24px] px-1.5 rounded-full bg-indigo-600 text-white font-mono font-bold text-xs shadow-xs">
                {selectedItems.length}
              </span>
              <span className="text-xs text-slate-200 font-medium">
                Đã chọn{" "}
                <strong className="text-white">
                  {formatNumber(
                    selectedItems.reduce((sum, i) => sum + i.remainingPlanQty, 0)
                  )}
                </strong>{" "}
                cái
              </span>
            </div>

            <div className="h-4 w-px bg-slate-700 hidden sm:block" />

            {/* Thông số tính toán Gang-Run */}
            {gangStats && (
              <div className="text-xs text-slate-300 hidden md:flex items-center gap-2">
                <span>
                  Tờ in lớn:{" "}
                  <strong className="font-mono text-emerald-400 font-bold">
                    {formatNumber(gangStats.totalRunSheetsNeeded)}
                  </strong>{" "}
                  tờ (bù hao {gangStats.allowanceSheets})
                </span>
                {homogeneity.primaryPaperSize && (
                  <Badge
                    variant="outline"
                    className="bg-slate-800 text-slate-300 border-slate-700 text-[10px] font-mono"
                  >
                    Khổ {homogeneity.primaryPaperSize}
                  </Badge>
                )}
              </div>
            )}

            {/* Cảnh báo Homogeneity Guard */}
            {!homogeneity.canCombine && selectedItems.length > 1 && (
              <div className="text-[11px] text-amber-300 flex items-center gap-1 bg-amber-950/60 px-2 py-1 rounded border border-amber-800/80">
                <AlertTriangle className="h-3 w-3 shrink-0 text-amber-400" />
                <span>{homogeneity.errors[0]}</span>
              </div>
            )}

            {/* Cụm nút thao tác */}
            <div className="flex items-center gap-2 shrink-0 ml-auto">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearSelection}
                className="h-8 text-xs text-slate-400 hover:text-white hover:bg-slate-800"
              >
                Hủy chọn
              </Button>

              {onPrepress && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => onPrepress(selectedItems)}
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-xs"
                  data-testid="pool-bulk-prepress"
                >
                  <Layers className="h-3.5 w-3.5 mr-1" />
                  {selectedItems.length > 1 ? `Bình file ghép (${selectedItems.length} SP)` : "Bình file"}
                </Button>
              )}

              {!allSelectedValid && (
                <span className="text-[11px] text-amber-300" data-testid="pool-needs-prepress">
                  Có dòng chưa có khổ in / số con — bình file trước khi lập lệnh
                </span>
              )}

              {/* Nút cho Single PO */}
              {allSelectedValid && selectedItems.length === 1 && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenSplitDialog(selectedItems[0])}
                    className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white font-medium"
                  >
                    <Scissors className="h-3.5 w-3.5 mr-1" /> Tách Lệnh
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleDispatchSingle}
                    className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-xs"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" /> Tạo Lệnh Đơn
                  </Button>
                </>
              )}

              {/* Nút cho Gang-Run PO */}
              {allSelectedValid && selectedItems.length > 1 && (
                <Button
                  type="button"
                  size="sm"
                  disabled={!homogeneity.canCombine}
                  onClick={handleDispatchGangRun}
                  className={cn(
                    "h-8 text-xs font-semibold shadow-xs",
                    homogeneity.canCombine
                      ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                      : "bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700"
                  )}
                >
                  <Factory className="h-3.5 w-3.5 mr-1" /> Tạo Lệnh Ghép (
                  {selectedItems.length} SP)
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DIALOG TÁCH LỆNH (SPLIT PO MODAL) */}
      <Dialog open={splitDialogOpen} onOpenChange={setSplitDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="h-4 w-4 text-indigo-600" /> Tách Đợt Lệnh Sản Xuất (Split PO)
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Khởi tạo Lệnh Sản Xuất cho một phần số lượng đơn hàng (chia đợt giao hoặc chia ca máy).
            </DialogDescription>
          </DialogHeader>

          {itemToSplit && (
            <div className="space-y-3.5 py-2 text-xs">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-1.5">
                <div className="font-semibold text-slate-800 text-sm">
                  {itemToSplit.productName}
                </div>
                <div className="text-slate-500 flex items-center gap-2">
                  <span>
                    Đơn hàng:{" "}
                    <strong className="font-mono text-slate-800">
                      {itemToSplit.orderCode}
                    </strong>
                  </span>
                  <span>•</span>
                  <span>KH: {itemToSplit.customerName}</span>
                </div>
                <div className="text-slate-600 flex items-center gap-3 pt-1 border-t border-slate-200/60 mt-1.5">
                  <span>
                    Tổng đặt:{" "}
                    <strong className="font-mono">
                      {formatNumber(itemToSplit.quantity)}
                    </strong>
                  </span>
                  <span>•</span>
                  <span>
                    Đã lên lệnh:{" "}
                    <strong className="font-mono">
                      {formatNumber(itemToSplit.plannedQty)}
                    </strong>
                  </span>
                  <span>•</span>
                  <span>
                    Chưa lên lệnh:{" "}
                    <strong className="font-mono text-indigo-700 font-bold">
                      {formatNumber(itemToSplit.remainingPlanQty)}
                    </strong>{" "}
                    {itemToSplit.unit || "cái"}
                  </span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="split-qty" className="text-xs font-semibold text-slate-700">
                  Số lượng muốn đưa vào Lệnh Sản Xuất này:
                </Label>
                <div className="relative">
                  <Input
                    id="split-qty"
                    type="number"
                    min={1}
                    max={itemToSplit.remainingPlanQty}
                    value={splitQtyInput}
                    onChange={(e) => setSplitQtyInput(parseInt(e.target.value) || 0)}
                    className="font-mono text-sm font-bold text-indigo-700 pr-14"
                  />
                  <span className="absolute right-3 top-2.5 text-xs text-slate-400 font-medium">
                    {itemToSplit.unit || "cái"}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Còn lại{" "}
                  <strong className="font-mono text-slate-800">
                    {formatNumber(Math.max(0, itemToSplit.remainingPlanQty - splitQtyInput))}
                  </strong>{" "}
                  {itemToSplit.unit || "cái"} sẽ tiếp tục nằm trong bể chờ cho các đợt lệnh sau.
                </p>
              </div>

              {/* Tính toán sơ bộ số tờ in */}
              {itemToSplit.effectiveUps && itemToSplit.effectiveUps > 0 && (
                <div className="p-2.5 rounded bg-indigo-50/70 border border-indigo-200/80 flex items-center justify-between text-indigo-900 text-xs">
                  <span>
                    Ước tính số tờ in chạy đợt này ({itemToSplit.effectiveUps} UPS):
                  </span>
                  <strong className="font-mono text-sm font-bold text-indigo-700">
                    {formatNumber(Math.ceil(splitQtyInput / itemToSplit.effectiveUps))} tờ
                  </strong>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSplitDialogOpen(false)}
              className="text-xs"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleConfirmSplit}
              className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
            >
              Xác Nhận Tách Lệnh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
